import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { templatesAPI } from '../../services/api';

const Templates = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [uploadingSatImage, setUploadingSatImage] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    primary_color: '#3b82f6',
    address_line_1: '',
    address_line_2: '',
    phone: '',
    footer_notes: '',
    validity_days: 30
  });

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await templatesAPI.get();
      const t = response.data.template;
      setTemplate(t);
      setFormData({
        company_name: t.company_name || '',
        primary_color: t.primary_color || '#3b82f6',
        address_line_1: t.address_line_1 || '',
        address_line_2: t.address_line_2 || '',
        phone: t.phone || '',
        footer_notes: t.footer_notes || '',
        validity_days: t.validity_days || 30
      });
    } catch (err) {
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await templatesAPI.update(formData);
      setSuccess('Configuración guardada exitosamente');
      fetchTemplate();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    setUploadingLogo(true);
    setError('');
    setSuccess('');

    try {
      await templatesAPI.uploadLogo(formData);
      setSuccess('Logo actualizado exitosamente');
      fetchTemplate();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('¿Eliminar el logo?\n\nLas cotizaciones futuras no mostrarán logo.')) return;

    setDeletingLogo(true);
    setError('');
    setSuccess('');

    try {
      await templatesAPI.deleteLogo();
      setSuccess('Logo eliminado exitosamente');
      fetchTemplate();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar el logo');
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSatImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('sat_image', file);

    setUploadingSatImage(true);
    setError('');
    setSuccess('');

    try {
      await templatesAPI.uploadSatImage(formData);
      setSuccess('Imagen SAT actualizada exitosamente');
      fetchTemplate();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al subir la imagen SAT');
    } finally {
      setUploadingSatImage(false);
    }
  };

  const API_URL = '';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Configuración de Plantilla PDF</h1>
          <p className="text-gray-600">Personaliza el diseño de las cotizaciones en PDF</p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
              {/* Company Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Información de la Empresa</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="label">Nombre de la Empresa</label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="input"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label">Dirección Línea 1</label>
                    <input
                      type="text"
                      value={formData.address_line_1}
                      onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                      className="input"
                      placeholder="Calle, número, colonia"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="label">Dirección Línea 2</label>
                    <input
                      type="text"
                      value={formData.address_line_2}
                      onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                      className="input"
                      placeholder="Ciudad, estado, código postal"
                    />
                  </div>
                  <div>
                    <label className="label">Teléfono</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input"
                      placeholder="55 1234 5678"
                    />
                  </div>
                  <div>
                    <label className="label">Color Principal</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="input flex-1"
                        placeholder="#3b82f6"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote Settings */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Cotización</h3>
                <div>
                  <label className="label">Vigencia por defecto (días)</label>
                  <input
                    type="number"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 30 })}
                    className="input w-32"
                    min="1"
                    max="365"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Número de días que la cotización será válida desde su emisión
                  </p>
                </div>
              </div>

              {/* Footer Notes */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Notas Importantes</h3>
                <div>
                  <label className="label">Notas al pie de la cotización</label>
                  <textarea
                    value={formData.footer_notes}
                    onChange={(e) => setFormData({ ...formData, footer_notes: e.target.value })}
                    className="input"
                    rows={6}
                    placeholder="1. El pago debe realizarse...&#10;2. La vigencia de la presente...&#10;3. El tiempo estimado de entrega..."
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Estas notas aparecerán al final de cada cotización PDF
                  </p>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end pt-4 border-t">
                <button type="submit" disabled={saving} className="btn btn-primary">
                  {saving ? 'Guardando...' : 'Guardar Configuración'}
                </button>
              </div>
            </form>
          </div>

          {/* Logo Section */}
          <div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Logo de la Empresa</h3>

              {/* Current Logo */}
              <div className="mb-4">
                {template?.logo_image ? (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img
                      src={`${API_URL}${template.logo_image}`}
                      alt="Logo actual"
                      className="max-w-full h-auto max-h-32 mx-auto"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 bg-gray-50 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">Sin logo</p>
                  </div>
                )}
              </div>

              {/* Upload */}
              <div className="space-y-2">
                <label className="block">
                  <span className="btn btn-secondary w-full cursor-pointer text-center">
                    {uploadingLogo ? 'Subiendo...' : 'Cambiar Logo'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="hidden"
                  />
                </label>
                {template?.logo_image && (
                  <button
                    type="button"
                    onClick={handleDeleteLogo}
                    disabled={deletingLogo}
                    className="btn w-full text-red-600 border-red-300 hover:bg-red-50"
                  >
                    {deletingLogo ? 'Eliminando...' : 'Eliminar Logo'}
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-2 text-center">
                  JPG, PNG, GIF, WebP o SVG. Máximo 5MB.
                </p>
              </div>
            </div>

            {/* SAT Image Section */}
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Imagen SAT (Constancia Fiscal)</h3>
              <p className="text-sm text-gray-500 mb-4">
                Esta imagen aparecerá en las cotizaciones PDF
              </p>

              {/* Current SAT Image */}
              <div className="mb-4">
                {template?.sat_image ? (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img
                      src={`${API_URL}${template.sat_image}`}
                      alt="Imagen SAT"
                      className="max-w-full h-auto max-h-40 mx-auto"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-8 bg-gray-50 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-500">Sin imagen SAT</p>
                  </div>
                )}
              </div>

              {/* Upload */}
              <div>
                <label className="block">
                  <span className="btn btn-secondary w-full cursor-pointer text-center">
                    {uploadingSatImage ? 'Subiendo...' : 'Cambiar Imagen SAT'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSatImageUpload}
                    disabled={uploadingSatImage}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  JPG, PNG o WebP. Máximo 5MB.
                </p>
              </div>
            </div>

            {/* Preview Card */}
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Vista Previa</h3>
              <div
                className="border rounded-lg p-4 text-sm"
                style={{ borderTopColor: formData.primary_color, borderTopWidth: '4px' }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-bold" style={{ color: formData.primary_color }}>
                      {formData.company_name || 'Nombre de Empresa'}
                    </p>
                    <p className="text-xs text-gray-500">{formData.address_line_1 || 'Dirección línea 1'}</p>
                    <p className="text-xs text-gray-500">{formData.address_line_2 || 'Dirección línea 2'}</p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <p>{formData.phone || 'Teléfono'}</p>
                    <p>Vigencia: {formData.validity_days} días</p>
                  </div>
                </div>
                <div className="border-t pt-2 text-xs text-gray-400">
                  <p className="font-medium mb-1">Notas:</p>
                  <p className="whitespace-pre-line line-clamp-3">
                    {formData.footer_notes || 'Las notas importantes aparecerán aquí...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </AdminLayout>
  );
};

export default Templates;
